import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PosPaymentErrorModal from './modals/PosPaymentErrorModal';
import GuideButton from '../../../common/guide/GuideButton';
import {
  verifyIngenicoPaymentAfterCommError,
  voidIngenicoOpenTransaction,
  IngenicoPaymentVerification,
} from '../hooks/fiscalDevices/ingenico';
import DepartmentVatMismatchModal from './modals/DepartmentVatMismatchModal';
import PosRefundModal, { hasPendingPosRefundsInStorage } from './modals/PosRefundModal';
import PavoPayRefundModal from './modals/PavoPayRefundModal';
import CustomerQueryModal from './modals/CustomerQueryModal';
import OtherPaymentMethodsModal from './modals/OtherPaymentMethodsModal';
import OnlinePaymentModal from './modals/OnlinePaymentModal';
import { ACCOUNTING_API } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';
import salesService from '../../../../services/salesService';

/**
 * PaymentFlowModals
 *
 * QuickSaleScreen'den taşınan ödeme akışı modalleri:
 *  - PosPaymentErrorModal (POS hata + split-payment iptal callback'leri)
 *  - DepartmentVatMismatchModal (Ingenico departman-KDV uyumsuzluğu)
 *  - Yazarkasa Bağlı Değil onay diyalogları (POS/Nakit ve Karma)
 *  - PosRefundModal / PavoPayRefundModal / CustomerQueryModal
 *  - OtherPaymentMethodsModal / OnlinePaymentModal
 *
 * Davranış birebir korunur: container koşulsuz render edilir, her modalın
 * orijinal guard'ı (open={...}) bu component içinde aynen yaşar.
 * Props-bag deseni RestaurantFlowModals.tsx ile uyumludur.
 */
interface PaymentFlowModalsProps {
  // POS Payment Error
  isPosErrorModalOpen: boolean;
  posPaymentError: any;
  posRetryCallback: any;
  posContinueCallback?: any; // ✅ YENİ (Ingenico): "Yine de Devam Et"
  handleClosePosErrorModal: any;
  handleClosePosErrorModalAndClearCart: any;
  isSplitPaymentMode: boolean;
  setIsSplitPaymentMode: any;
  setSplitPaymentSuccessCallback: any;
  setPendingSplitPayment: any;
  setPendingSplitCartItems: any;
  setPendingSplitActiveSaleId: any;
  splitPaymentResolveRef: any;
  isRestaurantMode: boolean;
  selectedFiscalDevice: any;
  getFiscalDevice: any;

  // Department VAT mismatch
  showDeptVatMismatchModal: boolean;
  setShowDeptVatMismatchModal: any;
  deptVatSyncResult: any;
  setDeptVatSyncResult: any;

  // Fiskal bağlı değil onayları
  isFiscalDisconnectedConfirmOpen: boolean;
  handleFiscalDisconnectedCancel: any;
  handleFiscalDisconnectedConfirm: any;
  isFiscalDisconnectedConfirmOpenPartial: boolean;
  handleFiscalDisconnectedCancelPartial: any;
  handleFiscalDisconnectedConfirmPartial: any;

  // ✅ YENİ (Ingenico): Yazarkasa HATASI sonrası "Yine de Devam Et" onayı (nakit akışı)
  isFiscalErrorContinueOpen?: boolean;
  fiscalErrorContinueMessage?: string;
  handleFiscalErrorContinueConfirm?: any;
  handleFiscalErrorContinueCancel?: any;

  // POS / PavoPay iade
  isPosRefundModalOpen: boolean;
  setIsPosRefundModalOpen: any;
  setHasPendingPosRefunds: any;
  companyId: any;
  screenSettings: any;
  showAlertModal: any;
  isPavoPayRefundModalOpen: boolean;
  setIsPavoPayRefundModalOpen: any;
  currentCashier: any;
  quickSaleUserData: any;

  // Cari sorgu (PavoPay)
  isCustomerQueryModalOpen: boolean;
  setIsCustomerQueryModalOpen: any;
  isRestP2PEnabled: boolean;
  cart: any[];
  setPavoPayTempCustomerParty: any;
  setPavoPayIsEInvoice: any;
  setPavoPayTempCustomerName: any;
  setPendingCustomerParty: any;
  setPendingIsEInvoice: any;
  setPendingNotificationPhone: any;
  activeBranchId: any;
  setSelectedCustomer: any;
  refreshCustomers: any;
  customers: any[];

  // Diğer ödeme yöntemleri
  otherPaymentModalOpen: boolean;
  setOtherPaymentModalOpen: any;
  otherPaymentOptions: any[];
  calculateTotal: any;
  companyNameCache: any;
  currentScreen: any;
  selectedCustomer: any;
  customersHook: any;
  mealCardFiscalData: any;
  sqlInventoryItems: any[];
  reservedLongCode: any;
  reservedShortCode: any;
  showSnackbar: any;
  tableId: any;
  tableName: any;
  waiterName: any;
  clearCart: any;
  refreshOrderNumber: any;
  generalCariRef: any;
  focusSearchBar: any;
  restaurantScreenId: any;
  navigate: any;
  printReceiptAfterPayment: any;

  // Online ödeme
  onlinePaymentModalOpen: boolean;
  setOnlinePaymentModalOpen: any;
  onlinePaymentOptions: any[];
  selectedWarehouseIds: any;

  // ✅ KULÜP GİRİŞİ — Dış bilet platformları (FIRSAT ME / BU BİLET)
  isClubEntryMode?: boolean;
  onExternalTicketSelected?: any;

  // ✅ YENİ: Sepete uygulanan indirim (fiş başlığına kaydetmek için)
  appliedDiscountType?: 'percentage' | 'amount' | null;
  lastAppliedDiscountAmount?: number;
}

const PaymentFlowModals: React.FC<PaymentFlowModalsProps> = ({
  isPosErrorModalOpen,
  posPaymentError,
  posRetryCallback,
  posContinueCallback,
  handleClosePosErrorModal,
  handleClosePosErrorModalAndClearCart,
  isSplitPaymentMode,
  setIsSplitPaymentMode,
  setSplitPaymentSuccessCallback,
  setPendingSplitPayment,
  setPendingSplitCartItems,
  setPendingSplitActiveSaleId,
  splitPaymentResolveRef,
  isRestaurantMode,
  selectedFiscalDevice,
  getFiscalDevice,
  showDeptVatMismatchModal,
  setShowDeptVatMismatchModal,
  deptVatSyncResult,
  setDeptVatSyncResult,
  isFiscalDisconnectedConfirmOpen,
  handleFiscalDisconnectedCancel,
  handleFiscalDisconnectedConfirm,
  isFiscalDisconnectedConfirmOpenPartial,
  handleFiscalDisconnectedCancelPartial,
  handleFiscalDisconnectedConfirmPartial,
  isFiscalErrorContinueOpen = false,
  fiscalErrorContinueMessage = '',
  handleFiscalErrorContinueConfirm,
  handleFiscalErrorContinueCancel,
  isPosRefundModalOpen,
  setIsPosRefundModalOpen,
  setHasPendingPosRefunds,
  companyId,
  screenSettings,
  showAlertModal,
  isPavoPayRefundModalOpen,
  setIsPavoPayRefundModalOpen,
  currentCashier,
  quickSaleUserData,
  isCustomerQueryModalOpen,
  setIsCustomerQueryModalOpen,
  isRestP2PEnabled,
  cart,
  setPavoPayTempCustomerParty,
  setPavoPayIsEInvoice,
  setPavoPayTempCustomerName,
  setPendingCustomerParty,
  setPendingIsEInvoice,
  setPendingNotificationPhone,
  activeBranchId,
  setSelectedCustomer,
  refreshCustomers,
  customers,
  otherPaymentModalOpen,
  setOtherPaymentModalOpen,
  otherPaymentOptions,
  calculateTotal,
  companyNameCache,
  currentScreen,
  selectedCustomer,
  customersHook,
  mealCardFiscalData,
  sqlInventoryItems,
  reservedLongCode,
  reservedShortCode,
  showSnackbar,
  tableId,
  tableName,
  waiterName,
  clearCart,
  refreshOrderNumber,
  generalCariRef,
  focusSearchBar,
  restaurantScreenId,
  navigate,
  printReceiptAfterPayment,
  onlinePaymentModalOpen,
  setOnlinePaymentModalOpen,
  onlinePaymentOptions,
  selectedWarehouseIds,
  isClubEntryMode = false,
  onExternalTicketSelected,
  appliedDiscountType = null,
  lastAppliedDiscountAmount = 0,
}) => {
  // ✅ YENİ (Ingenico): Nakit "Yazarkasa Hatası" modalında GetTicket cihaz sorgusu
  const [isCheckingFiscalDevice, setIsCheckingFiscalDevice] = useState(false);
  const [fiscalDeviceCheckResult, setFiscalDeviceCheckResult] = useState<IngenicoPaymentVerification | null>(null);
  // ✅ 'none' durumunda kullanıcı onayıyla fiş iptali (otomatik iptal YOK — doküman akışı)
  const [isVoidingFiscalTicket, setIsVoidingFiscalTicket] = useState(false);
  const [voidFiscalTicketResult, setVoidFiscalTicketResult] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    if (!isFiscalErrorContinueOpen) {
      setFiscalDeviceCheckResult(null);
      setIsCheckingFiscalDevice(false);
      setIsVoidingFiscalTicket(false);
      setVoidFiscalTicketResult(null);
    }
  }, [isFiscalErrorContinueOpen]);

  const handleCheckFiscalDevice = async () => {
    setIsCheckingFiscalDevice(true);
    setFiscalDeviceCheckResult(null);
    setVoidFiscalTicketResult(null);
    try {
      const verification = await verifyIngenicoPaymentAfterCommError();
      setFiscalDeviceCheckResult(verification);
    } catch {
      setFiscalDeviceCheckResult({ status: 'unknown' });
    } finally {
      setIsCheckingFiscalDevice(false);
    }
  };

  const handleVoidFiscalTicket = async () => {
    setIsVoidingFiscalTicket(true);
    setVoidFiscalTicketResult(null);
    try {
      const ok = await voidIngenicoOpenTransaction();
      setVoidFiscalTicketResult(ok ? 'success' : 'error');
    } catch {
      setVoidFiscalTicketResult('error');
    } finally {
      setIsVoidingFiscalTicket(false);
    }
  };

  return (
    <>
      {/* POS Payment Error Modal */}
      <PosPaymentErrorModal
        open={isPosErrorModalOpen}
        errorMessage={posPaymentError || 'Bilinmeyen hata'}
        onRetry={() => {
          if (posRetryCallback) {
            posRetryCallback();
          }
          handleClosePosErrorModal();
        }}
        onClose={() => {
          handleClosePosErrorModal();
          if (isSplitPaymentMode) {
            console.log('🔄 [SPLIT PAYMENT - POS ERROR] Sepet korunuyor, split payment modu kapatılıyor...');
            setIsSplitPaymentMode(false);
            setSplitPaymentSuccessCallback(undefined);
            setPendingSplitPayment(null);
            setPendingSplitCartItems([]);
            setPendingSplitActiveSaleId(null); // ✅ Override temizle
            if (splitPaymentResolveRef.current) {
              splitPaymentResolveRef.current(false);
              splitPaymentResolveRef.current = null;
            }
          }
        }}
        onCloseAndClearCart={() => {
          handleClosePosErrorModalAndClearCart();
          if (isSplitPaymentMode) {
            console.log('🔄 [SPLIT PAYMENT - POS ERROR] Sepet temizleniyor, split payment modu kapatılıyor...');
            setIsSplitPaymentMode(false);
            setSplitPaymentSuccessCallback(undefined);
            setPendingSplitPayment(null);
            setPendingSplitCartItems([]);
            setPendingSplitActiveSaleId(null); // ✅ Override temizle
            if (splitPaymentResolveRef.current) {
              splitPaymentResolveRef.current(false);
              splitPaymentResolveRef.current = null;
            }
          }
        }}
        isRestaurantMode={isRestaurantMode}
        selectedFiscalDevice={selectedFiscalDevice}
        onContinueAnyway={posContinueCallback ? () => {
          posContinueCallback();
          handleClosePosErrorModal();
        } : undefined}
        onCancelDocument={selectedFiscalDevice === 'ingenico_5000f' ? async () => {
          try {
            const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
            if (!fiscalDevice.cancelDocument) return false;
            const result = await fiscalDevice.cancelDocument();
            console.log('[Ingenico 5000F] Belge iptal sonucu:', result);
            return result.success;
          } catch (error) {
            console.error('[Ingenico 5000F] Belge iptal hatası:', error);
            return false;
          }
        } : undefined}
      />

      {/* Ingenico Departman-KDV Uyumsuzluk Modalı */}
      <DepartmentVatMismatchModal
        open={showDeptVatMismatchModal}
        onClose={() => setShowDeptVatMismatchModal(false)}
        syncResult={deptVatSyncResult}
        onFixed={() => {
          setDeptVatSyncResult(null);
          setShowDeptVatMismatchModal(false);
        }}
      />

      {/* ✅ YENİ: Yazarkasa Bağlı Değil Onay Modalı (POS / Nakit) */}
      <Dialog
        open={isFiscalDisconnectedConfirmOpen}
        onClose={handleFiscalDisconnectedCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2 
          }}>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              backgroundColor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: 48 }}>⚠️</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="warning.dark">
                Yazarkasa Bağlı Değil!
              </Typography>
              <GuideButton guideKey="sales.fiscalWarning" tabIndex={-1} />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Yazarkasa cihazına bağlanılamadı.
          </Typography>
          {/* ⚠️ Beko (300TR / X30TR) zorunlu mali cihazdır — bağlantı yokken
              "yine de devam et" akışı aktif olmamalıdır. Diğer cihazlarda
              (Hugin, Inpos, Ingenico, PavoPay) operatör onayıyla devam edilebilir. */}
          {(selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') ? (
            <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
              Beko yazarkasa zorunlu mali cihazdır. Bağlantı kurulmadan ödeme tamamlanamaz.
              <br />
              Lütfen yazarkasa bağlantısını kontrol edin ve tekrar deneyin.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" color="text.secondary">
                Ödeme işlemine <strong>yazarkasa olmadan</strong> devam etmek istiyor musunuz?
              </Typography>
              <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 2 }}>
                ⚠️ Bu işlem mali kayıt oluşturmayacaktır!
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: 'column', 
          gap: 2, 
          px: 3, 
          pb: 3,
          pt: 0
        }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            onClick={handleFiscalDisconnectedConfirm}
            // ⚠️ Beko cihazında "Yine de Devam Et" PASIF: mali kayıt zorunluluğu
            disabled={selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr'}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            ✅ EVET, DEVAM ET
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="large"
            fullWidth
            onClick={handleFiscalDisconnectedCancel}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            ❌ İPTAL
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ (Ingenico): Yazarkasa HATASI — Yine de Devam Et Onay Modalı (NAKİT) */}
      <Dialog
        open={isFiscalErrorContinueOpen}
        onClose={handleFiscalErrorContinueCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: 48 }}>⚠️</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="error.dark">
                Yazarkasa Hatası
              </Typography>
              <GuideButton guideKey="sales.fiscalWarning" tabIndex={-1} />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {fiscalErrorContinueMessage || 'Yazarkasa işlemi başarısız oldu.'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Ödemeyi fiilen aldıysanız <strong>"Yine de Devam Et"</strong> ile satışı sisteme kaydedebilirsiniz.
          </Typography>
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 2 }}>
            ⚠️ Bu seçenekte yazarkasadan YENİ fiş kesilmez; satış yalnızca sisteme kaydedilir.
          </Typography>

          {/* ✅ YENİ (Ingenico): GetTicket cihaz sorgu sonucu */}
          {fiscalDeviceCheckResult?.status === 'completed' && (
            <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ✅ Ödeme cihazda ALINMIŞ ({(fiscalDeviceCheckResult.totalPaymentTL ?? 0).toFixed(2)} TL) ve fiş kapatıldı.
              </Typography>
              <Typography variant="body2">
                "Yine de Devam Et" ile satışı sisteme kaydedin. Yeni fiş kesilmez.
              </Typography>
            </Alert>
          )}
          {fiscalDeviceCheckResult?.status === 'partial' && (
            <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ⚠️ Cihazda KISMİ ödeme var: {(fiscalDeviceCheckResult.totalPaymentTL ?? 0).toFixed(2)} TL alınmış,
                {' '}{(fiscalDeviceCheckResult.remainingAmountTL ?? 0).toFixed(2)} TL eksik.
              </Typography>
              <Typography variant="body2">
                Kalan tutarı cihazdan tamamlayın veya işlemi cihazdan iptal edin, sonra tekrar sorgulayın.
              </Typography>
            </Alert>
          )}
          {fiscalDeviceCheckResult?.status === 'none' && voidFiscalTicketResult === null && (
            <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ⚠️ Fişte henüz ödeme GÖRÜNMÜYOR ({(fiscalDeviceCheckResult.totalAmountTL ?? 0).toFixed(2)} TL açık fiş var). Fişe DOKUNULMADI.
              </Typography>
              <Typography variant="body2">
                Cihaz ödeme ekranında bekliyor olabilir. Cihaz ekranını kontrol edin:
                ödeme devam ediyorsa tamamlanmasını bekleyip tekrar sorgulayın;
                kesinlikle alınmadıysa "Fişi İptal Et" ile temizleyip yeniden deneyin.
              </Typography>
            </Alert>
          )}
          {voidFiscalTicketResult === 'success' && (
            <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ✅ Açık fiş iptal edildi. "İptal" deyip ödemeyi güvenle tekrar deneyebilirsiniz.
              </Typography>
            </Alert>
          )}
          {voidFiscalTicketResult === 'error' && (
            <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ❌ Fiş iptal edilemedi. Cihaz meşgul olabilir — cihazdaki işlemi sonuçlandırıp tekrar deneyin.
              </Typography>
            </Alert>
          )}
          {fiscalDeviceCheckResult?.status === 'unknown' && fiscalDeviceCheckResult?.hasOpenTransaction && (
            <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ⚠️ Cihazda AÇIK İŞLEM var ancak detayları okunamadı.
              </Typography>
              <Typography variant="body2">
                Cihaz ödeme ekranında meşgul olabilir. Cihazdaki işlemi sonuçlandırıp
                tekrar "Ödemeyi Kontrol Et" basın. Fişe dokunulmadı.
              </Typography>
            </Alert>
          )}
          {fiscalDeviceCheckResult?.status === 'unknown' && !fiscalDeviceCheckResult?.hasOpenTransaction && (
            <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ⚠️ Cihazda açık işlem bulunamadı — durum kesin doğrulanamadı.
              </Typography>
              <Typography variant="body2">
                Fiş çıktıysa "Yine de Devam Et", çıkmadıysa "İptal" deyip tekrar deneyin.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{
          flexDirection: 'column',
          gap: 2,
          px: 3,
          pb: 3,
          pt: 0
        }}>
          {/* ✅ YENİ (Ingenico): GetTicket ile cihaz durumunu sorgula */}
          {selectedFiscalDevice === 'ingenico_5000f' && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={isCheckingFiscalDevice ? <CircularProgress size={20} color="inherit" /> : <TravelExploreIcon />}
              onClick={handleCheckFiscalDevice}
              disabled={isCheckingFiscalDevice}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2,
                bgcolor: 'info.main',
                '&:hover': { bgcolor: 'info.dark' },
              }}
            >
              {isCheckingFiscalDevice ? 'CİHAZ SORGULANIYOR (GetTicket)...' : '🔎 ÖDEMEYİ KONTROL ET (CİHAZI SORGULA)'}
            </Button>
          )}
          {/* ✅ 'none': fişte ödeme görünmüyor → kullanıcı ONAYIYLA iptal */}
          {fiscalDeviceCheckResult?.status === 'none' && voidFiscalTicketResult !== 'success' && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={isVoidingFiscalTicket ? <CircularProgress size={20} color="inherit" /> : undefined}
              onClick={handleVoidFiscalTicket}
              disabled={isVoidingFiscalTicket || isCheckingFiscalDevice}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2,
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                '&:hover': { bgcolor: 'warning.dark' },
              }}
            >
              {isVoidingFiscalTicket ? 'FİŞ İPTAL EDİLİYOR...' : '🗑️ FİŞİ İPTAL ET (ÖDEME ALINMADIYSA)'}
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            onClick={handleFiscalErrorContinueConfirm}
            disabled={isCheckingFiscalDevice || isVoidingFiscalTicket}
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            ✅ YİNE DE DEVAM ET (SATIŞI KAYDET)
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="large"
            fullWidth
            onClick={handleFiscalErrorContinueCancel}
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            ❌ İPTAL (SATIŞI KAYDETME)
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Yazarkasa Bağlı Değil Onay Modalı (KARMA ÖDEME) */}
      <Dialog
        open={isFiscalDisconnectedConfirmOpenPartial}
        onClose={handleFiscalDisconnectedCancelPartial}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2 
          }}>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              backgroundColor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: 48 }}>⚠️</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="warning.dark">
                Yazarkasa Bağlı Değil!
              </Typography>
              <GuideButton guideKey="sales.fiscalWarning" tabIndex={-1} />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Yazarkasa cihazına bağlanılamadı.
          </Typography>
          {/* ⚠️ Beko (300TR / X30TR) zorunlu mali cihazdır — bağlantı yokken
              karma ödemenin "yine de devam et" akışı aktif olmamalıdır. */}
          {(selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') ? (
            <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
              Beko yazarkasa zorunlu mali cihazdır. Bağlantı kurulmadan karma ödeme tamamlanamaz.
              <br />
              Lütfen yazarkasa bağlantısını kontrol edin ve tekrar deneyin.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" color="text.secondary">
                Karma ödeme işlemine <strong>yazarkasa olmadan</strong> devam etmek istiyor musunuz?
              </Typography>
              <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 2 }}>
                ⚠️ Bu işlem mali kayıt oluşturmayacaktır!
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: 'column', 
          gap: 2, 
          px: 3, 
          pb: 3,
          pt: 0
        }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            onClick={handleFiscalDisconnectedConfirmPartial}
            // ⚠️ Beko cihazında "Yine de Devam Et" PASIF: mali kayıt zorunluluğu
            disabled={selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr'}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            ✅ EVET, DEVAM ET
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="large"
            fullWidth
            onClick={handleFiscalDisconnectedCancelPartial}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            ❌ İPTAL
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: POS İade Modal (Hugin/Inpos için) */}
      <PosRefundModal
        open={isPosRefundModalOpen}
        onClose={() => {
          setIsPosRefundModalOpen(false);
          // Modal kapandığında pending refunds'ı yenile
          setHasPendingPosRefunds(hasPendingPosRefundsInStorage(companyId));
        }}
        companyId={companyId}
        selectedFiscalDevice={screenSettings?.selectedFiscalDevice as 'hugin_t300' | 'inpos_m530' | 'beko_x30tr' | 'beko_300tr' | 'pavopay' | 'ingenico_5000f' || 'hugin_t300'}
        showAlertModal={showAlertModal}
      />
      
      {/* ✅ YENİ: PavoPay İade Modal */}
      <PavoPayRefundModal
        open={isPavoPayRefundModalOpen}
        onClose={() => setIsPavoPayRefundModalOpen(false)}
        companyId={companyId}
        showAlertModal={showAlertModal}
        createdBy={currentCashier?.cashierName || quickSaleUserData?.uid}
      />

      {/* PavoPay Cari Sorgu Modal */}
      <CustomerQueryModal
        open={isCustomerQueryModalOpen}
        onClose={() => setIsCustomerQueryModalOpen(false)}
        companyId={companyId || ''}
        isRestP2PEnabled={isRestP2PEnabled}
        onSelectForSale={async (customerParty: any, isEInvoice: any, displayName: any, notificationPhone: any) => {
          console.log('[PavoPay TempCustomer] Bu satış için kullan:', { displayName, isEInvoice, notificationPhone, cartLength: cart.length });
          setPavoPayTempCustomerParty(customerParty);
          setPavoPayIsEInvoice(isEInvoice);
          setPavoPayTempCustomerName(displayName);
          setPendingCustomerParty(customerParty);
          setPendingIsEInvoice(isEInvoice);
          setPendingNotificationPhone(notificationPhone || '');
          setIsCustomerQueryModalOpen(false);

          if (customerParty.TaxNumber && companyId) {
            try {
              const response = await accountingApi.post(ACCOUNTING_API.ACCOUNT_FIND_OR_CREATE_BY_TAX, {
                taxNumber: customerParty.TaxNumber,
                companyName: displayName,
                taxOffice: customerParty.TaxOfficeName || customerParty.TaxOfficeCode || '',
                authorizedPerson: customerParty.CustomerType === 1
                  ? `${customerParty.FirstName || ''} ${customerParty.FamilyName || ''}`.trim()
                  : displayName,
                phone: customerParty.Phone || '',
                email: customerParty.EMail || '',
                country: customerParty.Country || 'Türkiye',
                city: customerParty.City || '',
                district: customerParty.District || '',
                address: customerParty.Address || '',
                notes: isEInvoice ? 'E-Fatura Mükellefi (PavoPay)' : 'E-Arşiv (PavoPay)',
                branchId: activeBranchId || undefined,
                createdBy: currentCashier?.cashierName || quickSaleUserData?.uid || '',
              });

              const accountData = response.data?.data;
              if (accountData?.id) {
                const customer = {
                  id: String(accountData.id),
                  companyName: accountData.companyName || displayName,
                  balance: accountData.balance ?? 0,
                  debitBalance: accountData.debitBalance ?? 0,
                  creditBalance: accountData.creditBalance ?? 0,
                  mobilePhone: accountData.mobilePhone || '',
                  email: accountData.email || '',
                  isActive: accountData.isActive ?? true,
                  taxNumber: accountData.taxNumber || '',
                  taxOffice: accountData.taxOffice || '',
                  address: accountData.address || '',
                };
                setSelectedCustomer(customer);
                console.log('[PavoPay TempCustomer] Cari hesap bulundu/oluşturuldu ve seçildi:', { id: customer.id, name: customer.companyName, isNew: response.data?.isNew });
              }
            } catch (err: any) {
              console.error('[PavoPay TempCustomer] Cari bul/oluştur hatası:', err);
            }
          }
        }}
        onSaveAndSelect={async (customerParty: any, isEInvoice: any, savedAccountId: any, displayName: any, notificationPhone: any) => {
          setPavoPayTempCustomerParty(customerParty);
          setPavoPayIsEInvoice(isEInvoice);
          setPavoPayTempCustomerName(null);
          setPendingCustomerParty(customerParty);
          setPendingIsEInvoice(isEInvoice);
          setPendingNotificationPhone(notificationPhone || '');
          setIsCustomerQueryModalOpen(false);
          refreshCustomers();

          try {
            const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(parseInt(savedAccountId)));
            const accountData = response.data?.data;
            if (accountData?.id) {
              setSelectedCustomer({
                id: String(accountData.id),
                companyName: accountData.companyName || displayName,
                balance: accountData.balance ?? 0,
                debitBalance: accountData.debitBalance ?? 0,
                creditBalance: accountData.creditBalance ?? 0,
                mobilePhone: accountData.mobilePhone || '',
                email: accountData.email || '',
                isActive: accountData.isActive ?? true,
                taxNumber: accountData.taxNumber || '',
                taxOffice: accountData.taxOffice || '',
                address: accountData.address || '',
              });
              console.log('[PavoPay SaveAndSelect] Kaydedilen cari seçildi:', { id: accountData.id, name: accountData.companyName });
            }
          } catch (err: any) {
            console.error('[PavoPay SaveAndSelect] Cari getirme hatası, fallback denenecek:', err);
            setTimeout(() => {
              const saved = customers.find((c: any) => String(c.id) === String(savedAccountId));
              if (saved) setSelectedCustomer(saved);
            }, 1000);
          }
        }}
      />

      {/* ✅ YENİ: Diğer Ödeme Yöntemleri Modalı */}
      <OtherPaymentMethodsModal
        open={otherPaymentModalOpen}
        onClose={() => setOtherPaymentModalOpen(false)}
        otherPaymentOptions={otherPaymentOptions}
        total={calculateTotal()}
        // Ödeme işlemi için gerekli veriler
        cart={cart}
        companyId={companyId || ''}
        companyName={companyNameCache ?? undefined}
        screenId={currentScreen?.id ? parseInt(currentScreen.id.toString(), 10) : undefined}
        accountId={parseInt(selectedCustomer?.id || customersHook.generalCariRef?.id || '0', 10)}
        accountName={selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || 'Genel Cari'}
        screenSettings={screenSettings as any}
        isRestaurantMode={isRestaurantMode}
        cashierId={currentCashier?.cashierId}
        cashierName={currentCashier?.cashierName}
        createdBy={quickSaleUserData?.uid}
        // PavoPay yemek karti destegi
        selectedFiscalDevice={selectedFiscalDevice}
        usePavoPayForOtherPayments={screenSettings?.usePavoPayForOtherPayments === true}
        fiscalProducts={mealCardFiscalData.products}
        fiscalTotal={mealCardFiscalData.total}
        // ✅ KULÜP GİRİŞİ — Dış bilet platformları (FIRSAT ME / BU BİLET)
        isClubEntryMode={isClubEntryMode}
        onExternalTicketSelected={onExternalTicketSelected}
        // ✅ YENİ: Sepete uygulanan indirim (fiş başlığına kaydet)
        appliedDiscountType={appliedDiscountType}
        lastAppliedDiscountAmount={lastAppliedDiscountAmount}
        // Callbacks
        onPaymentComplete={async (refNo: any, successMessage: any, paymentLabel: any, otherCashboxId: any, otherCashboxName: any, otherInvoiceNumber: any) => {
          console.log('✅ [DİĞER ÖDEME] Tamamlandı, mutfak entegrasyonu kontrol ediliyor...', refNo);

          // 🍽️ MUTFAK EKRANI ENTEGRASYONU - Diğer Ödeme (Multinet, Sodexo, Ticket vs.)
          // Pattern: usePayment.ts NAKİT akışıyla bire bir aynı (satır ~2307-2437).
          // İki kol var:
          //  1) Normal Hızlı Satış: useKitchenScreen + selectedQRMenuId + !isRestaurantMode
          //  2) Restoran Modu: useKitchenScreen + selectedQRMenuId + isRestaurantMode (IsPaymentUpdate=true)
          // Cart snapshot'ı clearCart()'tan ÖNCE alıyoruz çünkü clearCart sonrası sepet boş olur.
          try {
            const cartSnapshot = cart.filter((i: any) => !i.id.startsWith('DEPOSIT-'));
            // ⚠️ KAYNAK: usePayment.ts (satır ~2311) ile aynı:
            //   `screenSettings.useKitchenScreen` (NOT `restaurantState.useKitchenScreen` —
            //   o sadece restoran modu için doluyor; normal hızlı satışta false kalıyor).
            const useKitchenScreenLocal = (screenSettings as any)?.useKitchenScreen || false;
            const selectedQRMenuIdLocal = (screenSettings as any)?.selectedQRMenuId || null;
            const odemeYontemi = paymentLabel || 'Diğer';

            console.log('[MUTFAK EKRANI] 🔎 Diğer ödeme - kontrol:', {
              useKitchenScreenLocal,
              selectedQRMenuIdLocal,
              isRestaurantMode,
              cartItems: cartSnapshot.length,
              odemeYontemi,
            });

            if (useKitchenScreenLocal && selectedQRMenuIdLocal && !isRestaurantMode) {
              console.log('[MUTFAK EKRANI] 🍽️ Diğer ödeme + Hızlı Satış — sipariş gönderiliyor...');

              const siparisDetaylari = cartSnapshot.map((item: any) => {
                const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === item.id);
                return {
                  UrunId: item.id,
                  UrunAdi: item.name,
                  BirimFiyat: item.price,
                  Miktar: item.quantity,
                  ToplamFiyat: item.price * item.quantity,
                  UrunResmi: inventoryItem?.imageUrl || null,
                  Aciklama: null,
                  CategoryId: inventoryItem?.categoryId || null,
                  VatRate: inventoryItem?.vatRate || null,
                };
              });

              const kitchenOrderRequest = {
                QRMenuId: selectedQRMenuIdLocal,
                PersonelFirebaseUid: quickSaleUserData?.uid || null,
                PersonelName: quickSaleUserData
                  ? `${quickSaleUserData.firstName || ''} ${quickSaleUserData.lastName || ''}`.trim() || 'Hızlı Satış'
                  : 'Hızlı Satış',
                ToplamTutar: calculateTotal(),
                SiparisDetaylari: siparisDetaylari,
                SiparisNotu: null,
                OdemeYontemi: odemeYontemi,
                IslemId: null,
                DekontId: null,
                QuickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id.toString(), 10) : null,
                ReferenceNumber: refNo || null,
                IsRestaurantMode: false,
                ReservedLongCode: reservedLongCode || null,
                ReservedShortCode: reservedShortCode || null,
                BranchId: activeBranchId || null,
              };

              const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);
              if (kitchenResponse.success) {
                console.log('[MUTFAK EKRANI] ✅ Diğer ödeme siparişi mutfağa gönderildi:', kitchenResponse.data?.shortCode);
                showSnackbar(`Sipariş mutfak ekranına gönderildi (${kitchenResponse.data?.shortCode})`, 'info');
              } else {
                console.error('[MUTFAK EKRANI] ❌ Diğer ödeme - mutfak gönderim başarısız:', kitchenResponse.message);
              }
            } else if (useKitchenScreenLocal && selectedQRMenuIdLocal && isRestaurantMode) {
              console.log('[MUTFAK EKRANI] 🍽️ Diğer ödeme + Restoran Modu — ödeme güncelleme/oluşturma...');

              const siparisDetaylari = cartSnapshot.map((item: any) => {
                const inventoryItem = sqlInventoryItems.find((inv: any) => inv.id === item.id);
                return {
                  UrunId: item.id,
                  UrunAdi: item.name,
                  BirimFiyat: item.price,
                  Miktar: item.quantity,
                  ToplamFiyat: item.price * item.quantity,
                  UrunResmi: inventoryItem?.imageUrl || null,
                  Aciklama: null,
                  CategoryId: inventoryItem?.categoryId || null,
                  VatRate: inventoryItem?.vatRate || null,
                };
              });

              const kitchenOrderRequest = {
                QRMenuId: selectedQRMenuIdLocal,
                PersonelFirebaseUid: quickSaleUserData?.uid || null,
                PersonelName: quickSaleUserData
                  ? `${quickSaleUserData.firstName || ''} ${quickSaleUserData.lastName || ''}`.trim() || 'Restoran Satışı'
                  : 'Restoran Satışı',
                ToplamTutar: calculateTotal(),
                SiparisDetaylari: siparisDetaylari,
                SiparisNotu: null,
                OdemeYontemi: odemeYontemi,
                IslemId: null,
                DekontId: null,
                QuickSaleScreenId: currentScreen?.id ? parseInt(currentScreen.id.toString(), 10) : null,
                ReferenceNumber: refNo || null,
                IsRestaurantMode: true,
                ReservedLongCode: reservedLongCode || null,
                ReservedShortCode: reservedShortCode || null,
                BranchId: activeBranchId || null,
                IsPaymentUpdate: true,
                TableName: tableName || null,
                CustomerAccountName: selectedCustomer?.companyName || null,
              };

              const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);
              if (kitchenResponse.success) {
                console.log('[MUTFAK EKRANI] ✅ Diğer ödeme - restoran mutfak güncelleme başarılı:', kitchenResponse.data?.shortCode);
              } else {
                console.error('[MUTFAK EKRANI] ❌ Diğer ödeme - restoran mutfak güncelleme başarısız:', kitchenResponse.message);
              }
            } else {
              console.log('[MUTFAK EKRANI] ℹ️ Diğer ödeme - mutfak entegrasyonu kapalı veya QR menü seçilmemiş');
            }
          } catch (kitchenError) {
            // Satış başarılı olduğu için kullanıcıya hata gösterme — sadece logla.
            console.error('[MUTFAK EKRANI] ❌ Diğer ödeme - mutfak entegrasyonu hatası:', kitchenError);
          }

          // ✅ YENİ: Restoran modunda adisyonu kapat (CompletedSales'e taşı) — NAKİT akışıyla bire bir aynı pattern.
          // Diğer ödeme yöntemleri yazarkasa kullanmaz; atomic processOtherPaymentWithInvoice ile fatura/cari/kasa/stok
          // çoktan kaydedildi. Burada sadece active sale → completed sale geçişi ve restoran ekranına dönüş yapılır.
          if (isRestaurantMode && tableId) {
            const closeTotal = calculateTotal();
            const label = paymentLabel || 'Diğer';
            console.log('[RESTORAN-DİĞER ÖDEME] 🍽️ Restoran modu aktif, adisyon kapatılıyor...', {
              tableId,
              closeTotal,
              label,
              otherCashboxId,
              otherCashboxName,
            });
            try {
              const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
              const numericTableId = parseInt(tableId, 10);
              const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
              const targetActiveSaleId = activeSale?.Id ?? null;

              if (targetActiveSaleId) {
                console.log('[RESTORAN-DİĞER ÖDEME] ✅ Aktif satış bulundu, ID:', targetActiveSaleId);

                const otherPaymentDetails = JSON.stringify({
                  otherAmount: closeTotal,
                  cashboxId: otherCashboxId || null,
                  cashboxName: otherCashboxName || null,
                  paymentProviderType: label,
                  paymentProviderName: label,
                });

                const completePayload = {
                  paymentMethod: label,
                  paymentDetailsJson: otherPaymentDetails,
                  totalAmount: closeTotal,
                  completedBy: quickSaleUserData?.uid || undefined,
                  completedByName: waiterName || currentCashier?.cashierName || undefined,
                  saleNumber: refNo || undefined,
                  invoiceNumber: otherInvoiceNumber || undefined,
                  referenceNumber: refNo || undefined,
                  closedByWaiterName: waiterName || undefined,
                  isFiscalReceipt: false,
                  payments: [{
                    paymentMethod: 'other',
                    amount: closeTotal,
                    cashboxId: otherCashboxId ? parseInt(otherCashboxId, 10) : undefined,
                    cashboxName: otherCashboxName || undefined,
                  }],
                };
                console.log('[RESTORAN-DİĞER ÖDEME] 📤 completeSale çağrılıyor...', { activeSaleId: targetActiveSaleId, payload: completePayload });

                const completeResult = await restaurantActiveSaleApi.completeSale(targetActiveSaleId, completePayload);
                console.log('[RESTORAN-DİĞER ÖDEME] 📥 completeSale yanıtı:', JSON.stringify(completeResult));

                if (completeResult.success) {
                  console.log('[RESTORAN-DİĞER ÖDEME] ✅ Adisyon başarıyla kapatıldı!', completeResult.data);
                  showSnackbar(`Masa kapatıldı - ${completeResult.data?.saleNumber}`, 'success');
                } else {
                  console.error('[RESTORAN-DİĞER ÖDEME] ❌ Adisyon kapatılamadı:', completeResult.message);
                  showSnackbar(`Uyarı: Adisyon kapatılamadı - ${completeResult.message}`, 'warning');
                }
              } else {
                console.warn('[RESTORAN-DİĞER ÖDEME] ⚠️ Bu masa için aktif satış bulunamadı, tableId:', numericTableId);
              }
            } catch (restaurantError: any) {
              console.error('[RESTORAN-DİĞER ÖDEME] ❌ Restoran adisyon kapatma hatası:', restaurantError);
              console.error('[RESTORAN-DİĞER ÖDEME] ❌ Hata detayı:', restaurantError?.response?.data || restaurantError?.message);
              showSnackbar('Uyarı: Adisyon kapatılırken hata oluştu, ancak ödeme başarılı.', 'warning');
            }
          }

          clearCart();
          refreshOrderNumber();
          setSelectedCustomer(generalCariRef || null);
          // ✅ Başarı bildirimini snackbar ile göster
          if (successMessage) {
            showSnackbar(successMessage, 'success');
          }
          // Focus'u arama barına getir
          focusSearchBar();

          // ✅ YENİ: Restoran modundaysa RestaurantScreen'e geri dön (nakit akışıyla aynı 500ms gecikme).
          if (isRestaurantMode && restaurantScreenId) {
            console.log('[RESTORAN] 🔙 Diğer ödeme tamamlandı, RestaurantScreen\'e geri dönülüyor...');
            setTimeout(() => {
              navigate(`/restaurant-screen/${restaurantScreenId}`);
            }, 500);
          }
        }}
        // ✅ YENİ: Fiş yazdırma callback'i
        onPrintReceipt={(invoiceNumber: any, paymentType: any, items: any) => {
          console.log('🖨️ [DİĞER ÖDEME] Fiş yazdırılıyor:', { invoiceNumber, paymentType });
          try {
            // items'ı QuickSaleInvoiceItem formatına dönüştür
            const invoiceItems = items.map((item: any) => ({
              id: '',
              itemId: '',
              itemName: item.itemName,
              quantity: item.quantity,
              unit: 'adet',
              unitPrice: item.unitPrice,
              vatRate: item.vatRate,
              sctRate: 0,
              discountRate: 0,
              lineTotal: item.grossTotal,
              netTotal: item.netTotal,
              vatAmount: item.vatAmount,
              sctAmount: 0,
              grossTotal: item.grossTotal,
            }));
            // ✅ FIX: reservedShortCode (HS-X) fişe de gönderilir. Mutfak siparişi
            // onPaymentComplete'te aynı ReservedShortCode ile oluşturulduğundan
            // fişteki SİPARİŞ numarası mutfak ekranıyla birebir eşleşir.
            // printReceiptAfterPayment içindeki useKitchenScreen kontrolü sayesinde
            // mutfak entegrasyonu kapalıysa numara fişe yazılmaz (nakit/POS ile aynı).
            printReceiptAfterPayment(invoiceNumber, paymentType, invoiceItems, undefined, reservedShortCode);
          } catch (err) {
            console.error('🖨️ [DİĞER ÖDEME] Fiş yazdırma hatası:', err);
          }
        }}
        // 🎫 NUMARA GARANTİSİ: Deneme başladıktan sonra başarısız olursa numara yakılır
        onPaymentFailed={() => {
          console.warn('🎫 [DİĞER ÖDEME] Ödeme denemesi başarısız — sipariş numarası yenileniyor...');
          refreshOrderNumber();
        }}
      />

      {/* ✅ YENİ: Online Ödeme Modalı */}
      <OnlinePaymentModal
        open={onlinePaymentModalOpen}
        onClose={() => setOnlinePaymentModalOpen(false)}
        onlinePaymentOptions={onlinePaymentOptions}
        total={calculateTotal()}
        // ✅ YENİ: Ödeme işlemi için gerekli props
        companyId={companyId || ''}
        companyName={companyNameCache || undefined}
        screenId={currentScreen?.id ? Number(currentScreen.id) : undefined}
        accountId={Number(selectedCustomer?.id || customersHook.generalCariRef?.id || 0)}
        accountName={selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || undefined}
        cart={cart}
        selectedWarehouseIds={selectedWarehouseIds}
        isRestaurantMode={isRestaurantMode}
        currentCashier={currentCashier ? { id: currentCashier.cashierId, name: currentCashier.cashierName } : null}
        // ✅ YENİ: Sepete uygulanan indirim (fiş başlığına kaydet)
        appliedDiscountType={appliedDiscountType}
        lastAppliedDiscountAmount={lastAppliedDiscountAmount}
        onPaymentComplete={(refNo: any, successMessage: any) => {
          console.log('✅ [ONLINE ÖDEME] Tamamlandı, sepet temizleniyor...', refNo);
          clearCart();
          refreshOrderNumber();
          setSelectedCustomer(generalCariRef || null);
          // ✅ Başarı bildirimini snackbar ile göster
          if (successMessage) {
            showSnackbar(successMessage, 'success');
          }
          // Focus'u arama barına getir
          focusSearchBar();
        }}
        // ✅ YENİ: Fiş yazdırma callback'i
        onPrintReceipt={async (invoiceNumber: any, paymentType: any, items: any) => {
          console.log('🖨️ [ONLINE ÖDEME] Fiş yazdırılıyor:', { invoiceNumber, paymentType, itemCount: items.length });
          try {
            // items'ı QuickSaleInvoiceItem formatına dönüştür
            const invoiceItems = items.map((item: any) => ({
              id: '',
              itemId: '',
              itemName: item.itemName,
              quantity: item.quantity,
              unit: 'adet',
              unitPrice: item.unitPrice,
              vatRate: item.vatRate,
              sctRate: 0,
              discountRate: 0,
              lineTotal: item.grossTotal,
              netTotal: item.netTotal,
              vatAmount: item.vatAmount,
              sctAmount: 0,
              grossTotal: item.grossTotal,
            }));
            console.log('🖨️ [ONLINE ÖDEME] invoiceItems hazırlandı:', invoiceItems.length);
            await printReceiptAfterPayment(invoiceNumber, paymentType, invoiceItems);
            console.log('🖨️ [ONLINE ÖDEME] printReceiptAfterPayment çağrıldı');
          } catch (err) {
            console.error('🖨️ [ONLINE ÖDEME] Fiş yazdırma hatası:', err);
          }
        }}
        // 🎫 NUMARA GARANTİSİ: Deneme başladıktan sonra başarısız olursa numara yakılır
        onPaymentFailed={() => {
          console.warn('🎫 [ONLINE ÖDEME] Ödeme denemesi başarısız — sipariş numarası yenileniyor...');
          refreshOrderNumber();
        }}
      />
    </>
  );
};

export default PaymentFlowModals;
